// `getActions` (`extension-alert-candidate-profile-actions-provider.service.ng1.ts:123`) — the
// SINGLE-candidate half of the same provider `bulkActions` ports the other half of. It decides
// the icon strip under a candidate's name on the profile screen.
//
// ⚠️ IT IS NOT THE BULK LIST WITH ONE ENTRY. Four differences, none of them cosmetic:
//   · Favor and Archive appear only `viaHunter`, and their icon AND title flip per candidate —
//     `fas fa-heart` against `far fa-heart` — so they cannot be constants the way bulk's are.
//   · Download and "Email candidate details to me" exist ONLY here.
//   · Export-to-CSV and the shown-filter rules exist only in bulk — and so did Export, until
//     SINGLE_PROFILE_EXPORT (cee70a51e8) made it a single-profile action too.
//   · Report is unconditional here, behind nothing but a handler.
import { PROFILE_ACTION_ORDER, ProfileAction } from './bulkActions.ts'

export { PROFILE_ACTION_ORDER, ProfileAction }

/** What an action needs from the candidate to draw itself. */
export interface ProfileActionEntity {
  favorite?: boolean | null
  hidden?: boolean | null
  /** The ATS id. Single-profile Export is unavailable without one — see `isAvailable`. */
  externalId?: string | null
}

export interface ProfileActionDefinition {
  name: ProfileAction
  order: number
  /** Per-entity, because Favor and Archive swap icon and word with the candidate's state. */
  getIcon: (entity: ProfileActionEntity) => string
  getTitle: (entity: ProfileActionEntity) => string
  /** Adds the `on` class. Only Favor and Archive have one. */
  isOn?: (entity: ProfileActionEntity) => boolean
  /**
   * A per-ENTITY gate on top of `display`, which is shared by every row drawn with this list
   * (cee70a51e8). Only Export has one: it links an EXISTING ATS record to a list and never
   * creates one, so a row without an ATS id cannot offer it.
   */
  isAvailable?: (entity: ProfileActionEntity) => boolean
  display: boolean
}

/** `isActionVisible` — the template's filter predicate: shared `display`, then `isAvailable`. */
export function isActionVisible(action: ProfileActionDefinition, entity: ProfileActionEntity | undefined): boolean {
  return action.display && (action.isAvailable === undefined || action.isAvailable(entity ?? {}))
}

/**
 * The flags each action sits behind. Every one is a view-model boolean ANDed with a feature
 * flag upstream (see `BulkActionFlags` for the full table) — resolved by the caller, which is
 * the only place that has `window.careero`.
 */
export interface ProfileActionFlags {
  /** `featureFlags.EXTENSION_DOWNLOAD_WOO_PROFILE_ENABLED`. */
  downloadWooProfileEnabled?: boolean
  /** True on the hunter screens only; gates Favor, Archive and Assign-to-job. */
  viaHunter?: boolean
  /** `featureFlags.ASSIGN_TO_JOB && careero.assignToJobEnabled`. */
  assignToJobEnabled?: boolean
  addToAtsJobEnabled?: boolean
  messageCandidatesEnabled?: boolean
  /** `featureFlags.SEND_CANDIDATE_DETAILS_TO_USER`. */
  sendCandidateDetailsEnabled?: boolean
  /** Upstream gates this on a handler existing, and the snippet always passes one. */
  reportEnrichmentEnabled?: boolean
  /** Add-to-automation is offered only for a list that came from an ATS job. */
  fromExtensionJob?: boolean
  /**
   * `singleProfileExportEnabled() && !!handlers.exportToAtsList` — see `singleProfileExportEnabled`
   * for the three-way condition. Every screen that draws this strip passes the handler upstream
   * (the two snippets and the two lists), so the caller resolves the flag alone.
   */
  singleProfileExportEnabled?: boolean
  /** `getExportTitle()` — `exportTitle(atsName)`, an identity rule: "Export to Bullhorn hotlist/tearsheet". */
  exportTitle?: string
  /**
   * `getAtsLabel($window)`, read when the actions are BUILT (e5c43e15e3: upstream's provider is a
   * singleton, so a label cached at construction outlives a tenant switch).
   */
  atsLabel?: string
  /** "Add to Bullhorn job" on Bullhorn, "Add to job" elsewhere. */
  addToJobTitle?: string
}

export function profileActions(flags: ProfileActionFlags = {}): ProfileActionDefinition[] {
  const actions: ProfileActionDefinition[] = []

  if (flags.downloadWooProfileEnabled === true) {
    actions.push({
      name: ProfileAction.Download,
      order: PROFILE_ACTION_ORDER[ProfileAction.Download],
      getIcon: () => 'fas fa-download',
      getTitle: () => 'Download Woo profile',
      display: true,
    })
  }

  if (flags.viaHunter === true) {
    actions.push({
      name: ProfileAction.Favor,
      order: PROFILE_ACTION_ORDER[ProfileAction.Favor],
      // ⚠️ SOLID AGAINST REGULAR, NOT TWO DIFFERENT GLYPHS: `fas fa-heart` filled means
      // favourited, `far fa-heart` outlined means not. Same for the archive pair, which does
      // change glyph — `fa-user-check` against `fa-user-slash`.
      isOn: (entity) => entity.favorite === true,
      getIcon: (entity) => (entity.favorite === true ? 'fas fa-heart' : 'far fa-heart'),
      getTitle: (entity) => (entity.favorite === true ? 'Unfavorite' : 'Favorite'),
      display: true,
    })

    actions.push({
      name: ProfileAction.Archive,
      order: PROFILE_ACTION_ORDER[ProfileAction.Archive],
      isOn: (entity) => entity.hidden === true,
      getIcon: (entity) => (entity.hidden === true ? 'fas fa-user-check' : 'fas fa-user-slash'),
      getTitle: (entity) => (entity.hidden === true ? 'Unarchive' : 'Archive'),
      display: true,
    })

    if (flags.assignToJobEnabled === true) {
      actions.push({
        name: ProfileAction.AssignToJob,
        order: PROFILE_ACTION_ORDER[ProfileAction.AssignToJob],
        getIcon: () => 'fas fa-user-plus',
        getTitle: () => 'Assign to job',
        display: true,
      })
    }
  }

  if (flags.addToAtsJobEnabled === true) {
    actions.push({
      name: ProfileAction.AddToJob,
      order: PROFILE_ACTION_ORDER[ProfileAction.AddToJob],
      getIcon: () => 'fas fa-layer-plus',
      getTitle: () => flags.addToJobTitle ?? 'Add to job',
      display: true,
    })
  }

  if (flags.messageCandidatesEnabled === true) {
    actions.push({
      name: ProfileAction.Message,
      order: PROFILE_ACTION_ORDER[ProfileAction.Message],
      getIcon: () => 'fas fa-envelope',
      getTitle: () => 'Send a message',
      display: true,
    })
  }

  if (flags.singleProfileExportEnabled === true) {
    actions.push({
      name: ProfileAction.Export,
      order: PROFILE_ACTION_ORDER[ProfileAction.Export],
      getIcon: () => 'fas fa-upload',
      getTitle: () => flags.exportTitle ?? 'Export',
      // Export only links an existing ATS record to the list — it never creates one.
      isAvailable: (entity) => entity.externalId !== undefined && entity.externalId !== null && entity.externalId !== '',
      display: true,
    })
  }

  if (flags.sendCandidateDetailsEnabled === true) {
    actions.push({
      name: ProfileAction.SendCandidateDetails,
      order: PROFILE_ACTION_ORDER[ProfileAction.SendCandidateDetails],
      getIcon: () => 'fas fa-share-square',
      getTitle: () => 'Email candidate details to me',
      display: true,
    })
  }

  if (flags.reportEnrichmentEnabled === true) {
    actions.push({
      name: ProfileAction.ReportEnrichment,
      order: PROFILE_ACTION_ORDER[ProfileAction.ReportEnrichment],
      getIcon: () => 'far fa-flag',
      getTitle: () => 'Report',
      display: true,
    })
  }

  if (flags.fromExtensionJob === true) {
    actions.push({
      name: ProfileAction.AddToAutomation,
      order: PROFILE_ACTION_ORDER[ProfileAction.AddToAutomation],
      getIcon: () => 'fas fa-wand-magic',
      getTitle: () => `Add to ${flags.atsLabel ?? 'ATS'} automation`,
      display: true,
    })
  }

  return actions
}

/**
 * `hostSearchAlertHiddenProfileActions` — a LinkedIn-sourced alert loses two of its row actions.
 *
 * ⚠️ THE LIST IS SHORTER THAN IT LOOKS. `ExportToCsv` is in the set upstream but commented out,
 * with a note that it was a deliberate decision not to align it with Export — so a port that
 * "completes" the set removes an action production still offers.
 *
 * ⚠️ AND EXPORT LEFT IT (cee70a51e8). As a single-row action Export exists only under
 * SINGLE_PROFILE_EXPORT, which deliberately ALLOWS it on LinkedIn — that is the feature: a
 * LinkedIn profile card exporting to a Bullhorn tearsheet. BULK export on LinkedIn stays hidden,
 * by `bulkActions`' own `notFromHostSearch`.
 */
const HOST_SEARCH_HIDDEN: ReadonlySet<string> = new Set([ProfileAction.Favor, ProfileAction.Archive])

export function hideHostSearchActions(
  actions: readonly ProfileActionDefinition[],
  hostSite: string | null | undefined,
): ProfileActionDefinition[] {
  // Same `!hostSite || hostSite !== Linkedin` shape as the bulk list: only LinkedIn is affected.
  if (hostSite === undefined || hostSite === null || hostSite === '' || hostSite !== 'Linkedin') {
    return [...actions]
  }
  return actions.map((action) => (HOST_SEARCH_HIDDEN.has(action.name) ? { ...action, display: false } : action))
}

/** `maxAlertEntityActionsDropdownThreshold` — the strip stays flat at five or fewer. */
export const ACTIONS_DROPDOWN_THRESHOLD = 5

/**
 * `initProfileActionsDisplay` — how many icons are drawn inline.
 *
 * ⚠️ FIVE ACTIONS SHOW FIVE ICONS EVEN THOUGH `maxActionAmount` IS 4. The cap only applies once
 * the count EXCEEDS the threshold of five; at exactly five the strip shows all of them and no
 * ellipsis. That is what the reference capture of the candidate profile is — download, add to
 * job, message, email details, report — and a port that applies the 4 unconditionally drops the
 * flag icon and grows a dropdown production does not have.
 */
export function inlineActionCount(
  actions: readonly ProfileActionDefinition[],
  maxActionAmount: number,
  isDropdownEnabled: boolean,
  entity?: ProfileActionEntity,
): number {
  if (!isDropdownEnabled) return maxActionAmount
  const displayable = actions.filter((action) => isActionVisible(action, entity)).length
  return displayable > ACTIONS_DROPDOWN_THRESHOLD ? maxActionAmount : displayable
}

/**
 * The icons drawn in the strip: visible FOR THIS ENTITY, ordered, capped. `entity` is optional
 * only for callers that build no per-entity action; omitting it treats every `isAvailable` gate
 * as an entity with no fields — Export then hides, which is the safe reading.
 */
export function inlineProfileActions(
  actions: readonly ProfileActionDefinition[],
  maxActionAmount: number,
  isDropdownEnabled: boolean,
  entity?: ProfileActionEntity,
): ProfileActionDefinition[] {
  return actions
    .filter((action) => isActionVisible(action, entity))
    .sort((a, b) => a.order - b.order)
    .slice(0, inlineActionCount(actions, maxActionAmount, isDropdownEnabled, entity))
}

/**
 * The rest, behind the ellipsis.
 *
 * ⚠️ THE MENU'S `limitTo` COUNTS FROM THE UNFILTERED ARRAY: `limitTo: (profileActions.length -
 * maxActionAmount) : maxActionAmount`. So when some actions are hidden the menu asks for more
 * entries than remain — harmless, `limitTo` stops at the end — but the count is NOT
 * "displayable minus shown". Slicing from `maxActionAmount` reproduces it, because the offset
 * is what decides which entries appear and the length only ever over-reaches.
 */
export function overflowProfileActions(
  actions: readonly ProfileActionDefinition[],
  maxActionAmount: number,
  isDropdownEnabled: boolean,
  entity?: ProfileActionEntity,
): ProfileActionDefinition[] {
  if (!showActionsDropdown(actions, isDropdownEnabled, entity)) return []
  return actions
    .filter((action) => isActionVisible(action, entity))
    .sort((a, b) => a.order - b.order)
    .slice(maxActionAmount)
}

/** `(profileActions | filter: ctrl.isActionVisible).length > threshold && isDropdownEnabled`. */
export function showActionsDropdown(
  actions: readonly ProfileActionDefinition[],
  isDropdownEnabled: boolean,
  entity?: ProfileActionEntity,
): boolean {
  if (!isDropdownEnabled) return false
  return actions.filter((action) => isActionVisible(action, entity)).length > ACTIONS_DROPDOWN_THRESHOLD
}

/** The three view-model fields every export rule reads. */
export interface ExportCapabilitySource {
  featureFlags?: Readonly<Record<string, boolean>> | undefined
  exportCandidatesEnabled?: boolean | undefined
  exportCandidatesToExistingListEnabled?: boolean | undefined
}

/**
 * `exportToExistingListEnabled()` — `featureFlags.EXPORT_CANDIDATES &&
 * careero.exportCandidatesToExistingListEnabled`: Export opens the list picker. Without it the
 * export goes straight to a NEW list, named after the hunter (lists) or the profile (profiles).
 */
export function exportToExistingListEnabled(model: ExportCapabilitySource): boolean {
  return model.featureFlags?.['EXPORT_CANDIDATES'] === true && model.exportCandidatesToExistingListEnabled === true
}

/**
 * `singleProfileExportEnabled()` (cee70a51e8) — THREE things, and the flag is only one of them:
 * `SINGLE_PROFILE_EXPORT`, AND `EXPORT_CANDIDATES`, AND the ATS's export capability (either of
 * its two paths). The flag is seeded OFF and enabled per tenant.
 */
export function singleProfileExportEnabled(model: ExportCapabilitySource): boolean {
  const flags = model.featureFlags
  if (flags?.['SINGLE_PROFILE_EXPORT'] !== true) return false
  if (flags['EXPORT_CANDIDATES'] !== true) return false
  return model.exportCandidatesEnabled === true || model.exportCandidatesToExistingListEnabled === true
}
